<template>
    <v-container class="px-12">
        <div class="title">About the Regenerative Medicine Standards Portal</div>
        <div class="my-3">
            This portal provides an interactive database of the current landscape of regenerative medicine standards, with details on existing and in-development standards relevant to the field, as well as community-identified standards
            areas of need. Given the constantly evolving nature of regenerative medicine, portal users are encouraged to:
        </div>
        <ul class="my-3 mx-12">
            <li>Provide feedback and comment on <a @click.stop="allStandards">individual standards and areas of need</a></li>
            <li><router-link to="/contact">Alert SCB</router-link> to any inconsistencies or updates that should be made</li>
            <li>
                <a
                    href="http://twitter.com/intent/tweet?text=New+@SCBRegenMed+portal+is+an+interactive+database+of+the+current+landscape+of+%23regenerativemedicine+%23standards+-+https://portal.standardscoordinatingbody.org"
                    target="_blank"
                    >Share this portal</a
                >
                with colleagues
            </li>
        </ul>
        <div class="my-12 px-12">
            <v-divider></v-divider>
        </div>
        <div class="title">Background</div>
        <ul class="my-3 mx-12">
            <li>
                <span class="font-weight-bold">2017:</span>
                FDA engaged Nexight Group and the Standards Coordinating Body (SCB) to support the advancement of regenerative medicine standards. This effort was in response to the
                <a href="https://www.congress.gov/bill/114th-congress/house-bill/34/text/pl?overview=closed" target="_blank" v-track>21st Century Cures Act</a>, which directs the U.S. Food and Drug Administration (FDA), in consultation with
                the National Institute of Standards and Technology (NIST) and industry stakeholders, to “coordinate and prioritize the development of standards and consensus definition of terms... [that] support, through regulatory
                predictability, the development, evaluation, and review of regenerative medicine therapies and regenerative advanced therapies.”
            </li>
            <li>
                <span class="font-weight-bold">2018:</span>
                To advance standards in regenerative medicine, it was first critical to identify existing and in-development standards relevant to the field. In Spring 2018, Nexight Group and SCB released the first version of
                <em>The Regenerative Medicine Standards Landscape</em> report (PDF). The report was designed to serve as a reference that individual organizations could use to identify available standards to improve their operations.
            </li>
            <li>
                <span class="font-weight-bold">2019:</span>
                In assessing the standards landscape as part of <em>The Regenerative Medicine Standards Landscape</em> report effort, the community identified a strong need for more standards specific to regenerative medicine. In response,
                SCB, in partnership with Nexight Group, developed the <em>Community Perspectives: Needed Standards in Regenerative Medicine</em> report (PDF), which outlined standards needs identified by the community that could have the
                greatest benefit to the field and improve the safety and quality of regenerative medicine products.
            </li>
            <li>
                <span class="font-weight-bold">2018–2020:</span>
                During this timeframe, both the landscape and needed standards reports were regularly updated to reflect new and/or revised standards and areas of need, as well as community input and prioritization.
            </li>
            <li>
                <span class="font-weight-bold">2021:</span>
                To increase the usability of these reports for the regenerative medicine community, SCB and Nexight Group reimagined both PDF reports as the <strong>Regenerative Medicine Standards Portal</strong>.
            </li>
        </ul>
        <div class="my-12 px-12">
            <v-divider></v-divider>
        </div>
        <div class="title">Nexight Group and the Standards Coordinating Body</div>
        <div class="my-3">
            <v-row>
                <v-col>
                    <a href="https://www.nexightgroup.com/" target="_blank" v-track>Nexight Group, LLC</a> is a small business based in Silver Spring, Maryland, with extensive experience helping smart leaders solve complex technical and
                    management problems. Nexight specializes in engaging diverse experts in discussions about gaps and needed solutions to advance scientific fields, establishing new collaborative organizations, developing roadmaps and
                    strategic plans, designing new business processes, and supporting technical education and outreach efforts. By focusing on impacts, Nexight excels at transforming powerful ideas into practical action.
                </v-col>
                <v-col cols="auto">
                    <a href="https://www.nexightgroup.com/" target="_blank" v-track>
                        <img :height="25" :src="require('@/assets/NG_logo_Gray-Orange_small.png')" alt="Nexight Group" />
                    </a>
                </v-col>
            </v-row>
        </div>
        <div class="my-3">
            <v-row>
                <v-col>
                    <a href="https://www.standardscoordinatingbody.org/" target="_blank" v-track>The Standards Coordinating Body (SCB)</a> is a non-profit organization with a mission to “coordinate the accelerated advancement and improved
                    awareness of the standards and best practices that address the rapidly evolving needs of the global regenerative medicine advanced therapy community.” SCB connects the regenerative medicine community to the standards
                    development process and occupies a unique niche within the regenerative medicine ecosystem: with no vested interest in a particular scientific, commercial, clinical, or policy approach, SCB is focused on facilitating the
                    advancement and increased use of standards in response to demonstrated need from diverse regenerative medicine stakeholders.
                </v-col>
                <v-col cols="auto">
                    <a href="https://www.standardscoordinatingbody.org/" target="_blank" v-track>
                        <img :height="75" :src="require('@/assets/SCB_WebLogo.png')" alt="Standards Coordinating Body | Regenerative Medicine" />
                    </a>
                </v-col>
            </v-row>
        </div>
    </v-container>
</template>

<script>
    export default {
        name: 'About',
        methods: {
            allStandards() {
                this.$store.commit('resetFilters', true);
                this.$store.dispatch('search').then(() => {
                    this.$router.push({ path: this.$store.get('searchPath') });
                });
            }
        }
    };
</script>